import { useSyncExternalStore } from 'react'

/**
 * Звуки ответа в прохождении урока — порт `academy-frontend` `src/lib/sound.ts`
 * (`playFeedbackSound`): плеер синтезирует их Web Audio, файлов нет. Правильный
 * ответ — восходящий «дзинь» (C5 → G5, синус), неправильный — нисходящее «бзз»
 * (G3 → D#3, квадрат). Числа — 1:1 с плеером, чтобы автор слышал то же, что ученик.
 *
 * Флаг вкл/выкл — свой у студии, в localStorage: `soundEnabled` профиля бэка — это
 * настройка ученика в плеере, и предпросмотр автора её трогать не должен.
 */
const STORAGE_KEY = 'oa-studio-sound'

const RAMP_FLOOR_GAIN = 0.0001
const NOTE_ATTACK = 0.02
const CORRECT_NOTES = [523.25, 783.99]
const INCORRECT_NOTES = [196, 155.56]
const NOTE_GAP = 0.1
const NOTE_DURATION = 0.2
const PEAK_GAIN = 0.2

const readStored = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'on'
  } catch {
    return false
  }
}

let enabled = readStored()
const listeners = new Set<() => void>()

export const isSoundEnabled = () => enabled

export const setSoundEnabled = (next: boolean) => {
  enabled = next
  try {
    localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off')
  } catch {
    // приватный режим — флаг живёт до перезагрузки
  }
  listeners.forEach((l) => l())
  // Вызов идёт из клика по кнопке — это и есть жест, внутри которого Safari даёт запустить звук.
  if (next) unlock()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** Флаг звука для кнопки в шапке редактора. */
export const useSoundEnabled = () => useSyncExternalStore(subscribe, isSoundEnabled)

// Контекст создаётся по первому звуку: браузер не даёт играть до жеста
// пользователя, а в тестах (jsdom) `AudioContext` нет вовсе.
let context: AudioContext | null = null
const getContext = (): AudioContext | null => {
  if (context) return context
  if (typeof window === 'undefined' || typeof window.AudioContext !== 'function') return null
  context = new window.AudioContext()
  return context
}

// Safari разрешает запустить звук только ВНУТРИ жеста пользователя, а звук ответа
// играет чуть позже — из эффекта. Поэтому контекст будится на первом же нажатии —
// но только при включённом звуке: по умолчанию он выключен, и без согласия автора
// контекст не создаётся вовсе.
const unlock = () => {
  if (!enabled) return
  const ctx = getContext()
  if (ctx && ctx.state === 'suspended') void ctx.resume()
}

if (typeof window !== 'undefined') {
  window.addEventListener('pointerdown', unlock, { passive: true })
  window.addEventListener('keydown', unlock)
}

export const playFeedbackSound = (isCorrect: boolean) => {
  if (!enabled) return
  const ctx = getContext()
  if (!ctx) return
  // Контекст, созданный вне обработчика клика (звук играет из эффекта вердикта),
  // браузер запускает «на паузе» — без resume() ноты ставятся в очередь и молчат.
  if (ctx.state === 'suspended') void ctx.resume()
  const now = ctx.currentTime + 0.02
  ;(isCorrect ? CORRECT_NOTES : INCORRECT_NOTES).forEach((freq, i) => {
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = isCorrect ? 'sine' : 'square'
    osc.frequency.value = freq
    const start = now + i * NOTE_GAP
    gain.gain.setValueAtTime(RAMP_FLOOR_GAIN, start)
    gain.gain.exponentialRampToValueAtTime(PEAK_GAIN, start + NOTE_ATTACK)
    gain.gain.exponentialRampToValueAtTime(RAMP_FLOOR_GAIN, start + NOTE_DURATION - NOTE_ATTACK)
    osc.connect(gain).connect(ctx.destination)
    osc.start(start)
    osc.stop(start + NOTE_DURATION)
  })
}

// Файловые звуки плеера (`academy-frontend` `public/sounds`): «Далее» —
// `NextBlockButton`, конец урока — `FinishedLessonModal`. Буферы грузятся один раз.
const buffers = new Map<string, Promise<AudioBuffer | null>>()

const loadBuffer = (ctx: AudioContext, url: string): Promise<AudioBuffer | null> => {
  let pending = buffers.get(url)
  if (!pending) {
    pending = fetch(url)
      .then((r) => r.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .catch(() => null)
    buffers.set(url, pending)
  }
  return pending
}

const playFile = (url: string) => {
  if (!enabled) return
  const ctx = getContext()
  if (!ctx) return
  if (ctx.state === 'suspended') void ctx.resume()
  void loadBuffer(ctx, url).then((buffer) => {
    if (!buffer || !enabled) return
    const source = ctx.createBufferSource()
    source.buffer = buffer
    source.connect(ctx.destination)
    source.start()
  })
}

/** Звук кнопки «Далее». */
export const playNextSound = () => playFile('/sounds/next.mp3')

/** Звук конца урока. */
export const playFinalSound = () => playFile('/sounds/final.mp3')
